import { useResponsive } from '../../hooks/useResponsive';

/**
 * ResponsiveTable: Displays data as table on desktop, cards on mobile/tablet
 *
 * @param {Array} data - Array of row objects
 * @param {Array} columns - Array of { key, label, render? }
 * @param {React.ComponentType} cardComponent - Custom card component for mobile (optional)
 * @param {Object} options - { emptyMessage, onRowClick, tableClassName, cardGap }
 */
export default function ResponsiveTable({
  data = [],
  columns = [],
  cardComponent: CardComponent = null,
  options = {},
}) {
  const { isDesktop } = useResponsive();
  const {
    emptyMessage = 'Nenhum item encontrado',
    onRowClick = null,
    tableClassName = '',
    cardGap = 16,
  } = options;

  if (data.length === 0) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '48px 20px',
          color: '#94a3b8',
          fontSize: 13,
          background: '#fff',
          borderRadius: 16,
          border: '1px solid #f1f5f9',
        }}
      >
        {emptyMessage}
      </div>
    );
  }

  // Desktop: Table layout
  if (isDesktop) {
    return (
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          border: '1px solid #f1f5f9',
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,.04)',
        }}
        className={tableClassName}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
          }}
        >
          <thead>
            <tr
              style={{
                background: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 700,
                    color: '#64748b',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr
                key={row.id || idx}
                onClick={() => onRowClick?.(row)}
                style={{
                  borderBottom:
                    idx < data.length - 1 ? '1px solid #f8fafc' : 'none',
                  cursor: onRowClick ? 'pointer' : 'default',
                  background: onRowClick ? '#fafbfc' : 'transparent',
                  '&:hover': onRowClick ? { background: '#f1f5f9' } : {},
                }}
              >
                {columns.map((col) => (
                  <td
                    key={`${row.id || idx}-${col.key}`}
                    style={{
                      padding: '14px 16px',
                      color: '#0f172a',
                    }}
                  >
                    {col.render
                      ? col.render(row[col.key], row, col)
                      : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Mobile/Tablet: Card layout
  if (CardComponent) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: cardGap,
        }}
      >
        {data.map((row, idx) => (
          <CardComponent
            key={row.id || idx}
            data={row}
            columns={columns}
            onClick={() => onRowClick?.(row)}
          />
        ))}
      </div>
    );
  }

  // Default card layout if no CardComponent provided
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: cardGap,
      }}
    >
      {data.map((row, idx) => (
        <div
          key={row.id || idx}
          onClick={() => onRowClick?.(row)}
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: '16px',
            border: '1px solid #f1f5f9',
            boxShadow: '0 1px 4px rgba(0,0,0,.04)',
            cursor: onRowClick ? 'pointer' : 'default',
          }}
        >
          {columns.map((col) => (
            <div
              key={col.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px',
                fontSize: 13,
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  color: '#64748b',
                  fontSize: 11,
                  textTransform: 'uppercase',
                }}
              >
                {col.label}
              </span>
              <span style={{ color: '#0f172a', textAlign: 'right' }}>
                {col.render ? col.render(row[col.key], row, col) : row[col.key]}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
